// Content for the general app-feedback survey linked from the "asking for
// feedback" lifecycle emails.
//
// A 1:1 port of the "Final Вашето мнение е от голямо значение за нас — обратна
// връзка за приложението Momfident" Google Form: the same ten questions in the
// same order, the same option wording, the same three required questions and
// the same "up to 2" cap on both multi-selects.
//
// Must match the backend: SURVEY_KEY_APP_FEEDBACK and the answer codes in
// survey.constants.ts. Only stable codes cross the wire; labels are localized
// here, the same arrangement as surveyFeedback.js.

export const SURVEY_KEY = "app_feedback_2026";

// Required question ids (validated on the frontend AND backend — mirrors
// REQUIRED_ANSWERS[SURVEY_KEY_APP_FEEDBACK]).
export const REQUIRED_QUESTIONS = [
  "ayaSupport",
  "recommend",
  "desiredFeatures",
];

/** Max selections on either multi-select question. */
export const MAX_MULTI_SELECT = 2;

const bg = {
  metaTitle: "Обратна връзка | Momfident",
  pageTitle:
    "Помогнете ни да направим Momfident още по-полезен! Вашето мнение е от голямо значение за нас!",
  pageSubtitle: "Отнема по-малко от 5 минути.",
  requiredNote: "* Задължителен въпрос",
  otherPlaceholder: "Друго...",
  textPlaceholder: "Споделете с няколко изречения…",
  missingUid:
    "Тази връзка е непълна. Моля, отворете анкетата през бутона в имейла, който получихте.",
  submit: "Изпрати",
  submitting: "Изпращане…",
  errorRequired: "Моля, отговорете на този въпрос.",
  errorOther: "Моля, опишете вашия отговор.",
  errorSubmit:
    "Изпращането не бе успешно. Опитайте отново или ни пишете на contact@momfident.io.",
  thanksTitle: "Благодарим ви за вашата обратна връзка!",
  thanksBody:
    "Високо оценяваме вашата ангажираност! С попълването на анкетата вие допринасяте за по-добра грижа и подкрепа на майките в България.",
  questions: [
    {
      id: "ayaSupport",
      type: "single",
      required: true,
      label:
        "Как оценявате подкрепата от психологическия асистент Ая в приложението Momfident?",
      options: [
        { value: "very_helpful", label: "Много полезна – надмина очакванията ми" },
        { value: "sufficient", label: "Достатъчна – отговори на очакванията ми" },
        {
          value: "insufficient",
          label: "Недостатъчна – не отговори на очакванията ми",
        },
        { value: "cannot_judge", label: "Не мога да преценя" },
      ],
    },
    {
      id: "appEase",
      type: "single",
      required: false,
      label: "Колко лесно е използването на приложението Momfident?",
      options: [
        { value: "very_easy", label: "Много лесно" },
        { value: "fairly_easy", label: "Сравнително лесно" },
        { value: "slightly_confusing", label: "Малко объркващо" },
        { value: "hard_confusing", label: "Трудно и объркващо" },
      ],
    },
    {
      id: "assistantComfort",
      type: "single",
      required: false,
      label:
        "Колко комфортно се чувствате да говорите с виртуален асистент на емоционални теми?",
      options: [
        { value: "very_comfortable", label: "Много комфортно" },
        { value: "somewhat_comfortable", label: "Донякъде комфортно" },
        { value: "not_comfortable", label: "Не се чувствам комфортно" },
      ],
    },
    {
      id: "recommend",
      type: "single",
      required: true,
      label: "Бихте ли препоръчали Momfident на приятел?",
      options: [
        { value: "yes", label: "Да" },
        { value: "no", label: "Не" },
        { value: "not_sure", label: "Не съм сигурна" },
      ],
    },
    {
      id: "feelingAfter",
      type: "single",
      required: false,
      label: "Как се почувствахте след използването на Momfident?",
      options: [
        {
          value: "much_more_confident",
          label: "Почувствах се много по-уверена и спокойна",
        },
        {
          value: "somewhat_more_confident",
          label: "Почувствах се донякъде по-уверена и спокойна",
        },
        {
          value: "some_support_still_unsure",
          label: "Получих известна подкрепа, но все още се чувствам несигурна",
        },
        {
          value: "not_understood",
          label: "Не се почувствах разбрана или подкрепена",
        },
      ],
    },
    {
      id: "desiredFeatures",
      otherId: "desiredFeaturesOther",
      type: "multi",
      required: true,
      maxSelect: MAX_MULTI_SELECT,
      allowOther: true,
      label:
        "Какви нови функционалности бихте искали да получавате в бъдеще в приложението Momfident?",
      help: "Моля, изберете до 2 най-желани отговора!",
      options: [
        { value: "reliable_topics", label: "Нови теми за разговор" },
        { value: "voice_aya", label: "Гласова комуникация с Ая" },
        {
          value: "personal_specialist_chat",
          label: "Личен чат с подходящ доверен специалист спрямо въпроса ми",
        },
        {
          value: "anonymous_chat",
          label:
            "Анонимен чат с други майки, които преминават през подобни предизвикателства",
        },
        {
          value: "emotion_journal",
          label: "Водене на дневник за проследяване на емоциите ми",
        },
        {
          value: "psych_tests",
          label: "Психологични тестове, с които да разбера повече за себе си",
        },
        { value: "none", label: "Никое от посочените" },
      ],
    },
    {
      id: "selfHelpResources",
      otherId: "selfHelpResourcesOther",
      type: "multi",
      required: false,
      maxSelect: MAX_MULTI_SELECT,
      allowOther: true,
      label: "По принцип какъв тип ресурси за самопомощ предпочитате?",
      help: "Моля, изберете топ 2!",
      options: [
        {
          value: "reflection_reminders",
          label: "Напомняния за саморефлексия и утвърждения",
        },
        {
          value: "mother_stories",
          label: "Истории и личен опит, споделени от други майки",
        },
        {
          value: "coping_techniques",
          label:
            "Практични техники за справяне (дишане, водене на дневник, медитация)",
        },
        {
          value: "resource_guidance",
          label: "Насочване към полезни ресурси – спешни линии, специалисти",
        },
        {
          value: "selfcare_reminders",
          label: "Напомняния за грижа за себе си и изграждане на навици",
        },
        {
          value: "expert_content",
          label:
            "Експертно съдържание за психичното здраве (факти, статии, видеа и др.)",
        },
        { value: "none", label: "Никое от посочените" },
      ],
    },
    {
      id: "interviewOptIn",
      type: "single",
      required: false,
      label:
        "Бихте ли се включили в 10 минутен разговор с нашия екип, за получаване на допълнителна обратна връзка?",
      options: [
        { value: "yes", label: "да" },
        { value: "no", label: "не" },
      ],
    },
    {
      id: "likeMost",
      type: "text",
      required: false,
      label: "Какво ви харесва?",
    },
    {
      id: "improveMost",
      type: "text",
      required: false,
      label: "Какво можем да подобрим?",
    },
  ],
};

const en = {
  metaTitle: "Feedback | Momfident",
  pageTitle:
    "Help us make Momfident even more useful! Your opinion matters a lot to us!",
  pageSubtitle: "It takes less than 5 minutes.",
  requiredNote: "* Required question",
  otherPlaceholder: "Other...",
  textPlaceholder: "A couple of sentences is plenty…",
  missingUid:
    "This link is incomplete. Please open the survey using the button in the email you received.",
  submit: "Submit",
  submitting: "Submitting…",
  errorRequired: "Please answer this question.",
  errorOther: "Please describe your answer.",
  errorSubmit:
    "Submission failed. Please try again, or write to us at contact@momfident.io.",
  thanksTitle: "Thank you for your feedback!",
  thanksBody:
    "We highly value your engagement! By completing the survey you contribute to better care and support for mothers in Bulgaria.",
  questions: [
    {
      id: "ayaSupport",
      type: "single",
      required: true,
      label:
        "How would you rate the support from the psychological assistant Aya in the Momfident app?",
      options: [
        {
          value: "very_helpful",
          label: "Very helpful – exceeded my expectations",
        },
        { value: "sufficient", label: "Sufficient – met my expectations" },
        {
          value: "insufficient",
          label: "Insufficient – did not meet my expectations",
        },
        { value: "cannot_judge", label: "I can’t tell" },
      ],
    },
    {
      id: "appEase",
      type: "single",
      required: false,
      label: "How easy is the Momfident app to use?",
      options: [
        { value: "very_easy", label: "Very easy" },
        { value: "fairly_easy", label: "Fairly easy" },
        { value: "slightly_confusing", label: "A little confusing" },
        { value: "hard_confusing", label: "Difficult and confusing" },
      ],
    },
    {
      id: "assistantComfort",
      type: "single",
      required: false,
      label:
        "How comfortable do you feel talking to a virtual assistant about emotional topics?",
      options: [
        { value: "very_comfortable", label: "Very comfortable" },
        { value: "somewhat_comfortable", label: "Somewhat comfortable" },
        { value: "not_comfortable", label: "I don’t feel comfortable" },
      ],
    },
    {
      id: "recommend",
      type: "single",
      required: true,
      label: "Would you recommend Momfident to a friend?",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "not_sure", label: "I’m not sure" },
      ],
    },
    {
      id: "feelingAfter",
      type: "single",
      required: false,
      label: "How did you feel after using Momfident?",
      options: [
        {
          value: "much_more_confident",
          label: "I felt much more confident and calm",
        },
        {
          value: "somewhat_more_confident",
          label: "I felt somewhat more confident and calm",
        },
        {
          value: "some_support_still_unsure",
          label: "I got some support, but I still feel unsure",
        },
        {
          value: "not_understood",
          label: "I did not feel understood or supported",
        },
      ],
    },
    {
      id: "desiredFeatures",
      otherId: "desiredFeaturesOther",
      type: "multi",
      required: true,
      maxSelect: MAX_MULTI_SELECT,
      allowOther: true,
      label:
        "What new features would you like to have in the Momfident app in the future?",
      help: "Please choose up to 2 of your most desired answers!",
      options: [
        { value: "reliable_topics", label: "New conversation topics" },
        { value: "voice_aya", label: "Voice communication with Aya" },
        {
          value: "personal_specialist_chat",
          label:
            "Personal chat with a suitable trusted specialist for my question",
        },
        {
          value: "anonymous_chat",
          label:
            "Anonymous chat with other mothers going through similar challenges",
        },
        {
          value: "emotion_journal",
          label: "Keeping a journal to track my emotions",
        },
        {
          value: "psych_tests",
          label: "Psychological tests to learn more about myself",
        },
        { value: "none", label: "None of the above" },
      ],
    },
    {
      id: "selfHelpResources",
      otherId: "selfHelpResourcesOther",
      type: "multi",
      required: false,
      maxSelect: MAX_MULTI_SELECT,
      allowOther: true,
      label: "In general, what type of self-help resources do you prefer?",
      help: "Please choose your top 2!",
      options: [
        {
          value: "reflection_reminders",
          label: "Reminders for self-reflection and affirmations",
        },
        {
          value: "mother_stories",
          label: "Stories and personal experiences shared by other mothers",
        },
        {
          value: "coping_techniques",
          label:
            "Practical coping techniques (breathing, journaling, meditation)",
        },
        {
          value: "resource_guidance",
          label: "Guidance to useful resources – helplines, specialists",
        },
        {
          value: "selfcare_reminders",
          label: "Reminders for self-care and building habits",
        },
        {
          value: "expert_content",
          label:
            "Expert mental health content (facts, articles, videos and more)",
        },
        { value: "none", label: "None of the above" },
      ],
    },
    {
      id: "interviewOptIn",
      type: "single",
      required: false,
      label:
        "Would you join a 10-minute call with our team to give us further feedback?",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
    {
      id: "likeMost",
      type: "text",
      required: false,
      label: "What do you like?",
    },
    {
      id: "improveMost",
      type: "text",
      required: false,
      label: "What could we improve?",
    },
  ],
};

export const appFeedbackContent = { bg, en };
